// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import spaceProductService from "../../services/spaceProductService";
import { productService } from "../../services/productService";

const toArray = (res) =>
  Array.isArray(res) ? res : (res?.items ?? res?.data ?? res?.results ?? []);

// Per-space product list. Admin picks catalog products for a space and sets a
// min/max quantity and a "mandatory" flag per product. The pick list is limited
// to products bookable for this scenario (best-effort, via bookable_for_scenarios).
const SpaceProductConfig = ({ spaceId, scenarioCode }) => {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picks, setPicks] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  // Load the space's configured products.
  const reload = useCallback(() => {
    if (!spaceId) {
      setRows([]);
      return;
    }
    setLoading(true);
    spaceProductService
      .getSpaceProducts(spaceId)
      .then((res) => setRows(toArray(res)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [spaceId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Load the catalog (paged at 100 — the API's max page size), filtered to this
  // scenario's bookable products.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        // Up to 5 pages (500 products) — enough for a pick list.
        for (let page = 1; page <= 5; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const res = await productService.getBookableProducts({
            page,
            limit: 100,
          });
          const batch = toArray(res);
          all.push(...batch);
          if (batch.length < 100) break;
        }
        if (cancelled) return;
        let list = all;
        if (scenarioCode) {
          const filtered = list.filter(
            (p) =>
              Array.isArray(p.bookable_for_scenarios) &&
              p.bookable_for_scenarios.includes(scenarioCode),
          );
          // Fall back to the full active list if the scenario has no tagged
          // products (so the picker is never mysteriously empty).
          if (filtered.length > 0) list = filtered;
        }
        setCatalog(list);
      } catch {
        if (!cancelled) setCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scenarioCode]);

  const assignedIds = useMemo(
    () => new Set(rows.map((r) => r.product_catalog_id)),
    [rows],
  );
  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog
      .filter((p) => !assignedIds.has(p.id))
      .filter(
        (p) => !q || (p.vhp_name || p.name || "").toLowerCase().includes(q),
      );
  }, [catalog, assignedIds, search]);

  const togglePick = (id) =>
    setPicks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Add every ticked product in one go.
  const addSelected = async () => {
    if (picks.size === 0) return;
    setBusy(true);
    try {
      await Promise.all(
        Array.from(picks).map((id) =>
          spaceProductService.addSpaceProduct(spaceId, {
            product_catalog_id: Number(id),
            min_quantity: 1,
            max_quantity: 1,
            is_mandatory: false,
          }),
        ),
      );
      setPicks(new Set());
      setSearch("");
      reload();
    } catch (e) {
      toast.error(e?.message || "Could not add products");
      reload();
    } finally {
      setBusy(false);
    }
  };

  // Persist a field change for one product row (optimistic local update).
  const patchRow = async (productId, patch) => {
    setRows((prev) =>
      prev.map((r) =>
        r.product_catalog_id === productId ? { ...r, ...patch } : r,
      ),
    );
    try {
      await spaceProductService.updateSpaceProduct(spaceId, productId, patch);
    } catch (e) {
      toast.error(e?.message || "Could not update product");
      reload();
    }
  };

  const removeProduct = async (productId) => {
    try {
      await spaceProductService.removeSpaceProduct(spaceId, productId);
      reload();
    } catch (e) {
      toast.error(e?.message || "Could not remove product");
    }
  };

  if (!spaceId) {
    return (
      <p className="text-xs text-gray-500 dark:text-slate-400">
        Save the space (Szenario speichern) to attach products.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {loading && (
        <p className="text-xs text-gray-500 inline-flex items-center gap-1">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </p>
      )}

      {rows.length === 0 && !loading && (
        <p className="text-xs text-gray-500 dark:text-slate-400">
          No products yet.
        </p>
      )}

      {rows.map((r) => (
        <div
          key={r.product_catalog_id}
          className="rounded-md border border-gray-200 dark:border-slate-600 p-2 space-y-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-800 dark:text-slate-200 truncate">
              {r.product_name || `Product ${r.product_catalog_id}`}
              {r.product_type ? (
                <span className="text-xs text-gray-400 ml-1">
                  · {r.product_type}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={() => removeProduct(r.product_catalog_id)}
              className="text-red-600 hover:text-red-700"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              Min
              <input
                type="number"
                min={0}
                value={r.min_quantity ?? 1}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x) =>
                      x.product_catalog_id === r.product_catalog_id
                        ? { ...x, min_quantity: e.target.value }
                        : x,
                    ),
                  )
                }
                onBlur={() =>
                  patchRow(r.product_catalog_id, {
                    min_quantity: Math.max(
                      0,
                      parseInt(r.min_quantity, 10) || 0,
                    ),
                  })
                }
                className="w-14 px-1 py-0.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded"
              />
            </label>
            <label className="flex items-center gap-1">
              Max
              <input
                type="number"
                min={1}
                value={r.max_quantity ?? 1}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x) =>
                      x.product_catalog_id === r.product_catalog_id
                        ? { ...x, max_quantity: e.target.value }
                        : x,
                    ),
                  )
                }
                onBlur={() =>
                  patchRow(r.product_catalog_id, {
                    max_quantity: Math.max(
                      1,
                      parseInt(r.max_quantity, 10) || 1,
                    ),
                  })
                }
                className="w-14 px-1 py-0.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded"
              />
            </label>
            <label className="flex items-center gap-1 ml-auto">
              <input
                type="checkbox"
                checked={!!r.is_mandatory}
                onChange={(e) =>
                  patchRow(r.product_catalog_id, {
                    is_mandatory: e.target.checked,
                  })
                }
              />
              Mandatory
            </label>
          </div>
        </div>
      ))}

      {/* Multi-select: tick several products, add them together. */}
      <div className="pt-1 space-y-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded"
        />
        <div className="max-h-44 overflow-y-auto rounded border border-gray-200 dark:border-slate-600 divide-y divide-gray-100 dark:divide-slate-700">
          {available.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-slate-400 p-2">
              No more products to add.
            </p>
          ) : (
            available.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
              >
                <input
                  type="checkbox"
                  checked={picks.has(p.id)}
                  onChange={() => togglePick(p.id)}
                />
                <span className="truncate text-gray-800 dark:text-slate-200">
                  {p.vhp_name || p.name || `Product ${p.id}`}
                  {p.type ? (
                    <span className="text-xs text-gray-400 ml-1">
                      · {p.type}
                    </span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={addSelected}
          disabled={picks.size === 0 || busy}
          className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Add selected{picks.size > 0 ? ` (${picks.size})` : ""}
        </button>
      </div>
    </div>
  );
};

export default SpaceProductConfig;
